import SimulationSummary from 'components/Modal/BankingRenegociacao/components/SimulationSummary';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadRenegotiationLogs } from 'services/log-cobranca';
import { EmptyMessage, Load } from 'ui/styled';
import { normalizeResponse } from 'utils';
import { CANCELLED_STATUS } from 'utils/billing';
import { parseCurrency } from 'utils/parsers';
import {
  ListContainer,
  ListFallback,
  RenegotiationBlock,
  RenegotiationHeader,
  RenegotiationMeta,
  UnavailableNote,
} from './styled';

// Renegociacoes geradas por esta cobranca que foram canceladas. O backend as
// mantem em `renegociacoes` (com `status: cancelado`) para nao perder o
// vinculo, mas a original voltou a ser cobrada por inteiro: na original elas
// nao aparecem na aba, e sozinhas nao fazem a aba aparecer.
const cancelledIds = (row) =>
  (row?.renegotiations || [])
    .filter((item) => item?.status === CANCELLED_STATUS)
    .map((item) => Number(item?.id));

// Tem renegociacao para mostrar? Decide se a aba aparece no Historico.
export const hasRenegotiation = (row) =>
  !!(
    row?.renegotiationOrigin ||
    (row?.renegotiations || []).some(
      (item) => item?.status !== CANCELLED_STATUS
    ) ||
    row?.hasRenegotiatedTitles
  );

// Logs gravados antes de 20/10/2026 guardam os parametros em
// `dados.parametros`, com `entrada` no lugar de `valor_entrada`. Os novos vem
// planos. A aba aceita os dois.
const readParams = (log) => {
  const dados = log?.dados || {};
  if (!dados.parametros) {
    return dados;
  }
  return {
    ...dados,
    ...dados.parametros,
    valor_entrada: dados.parametros.valor_entrada ?? dados.parametros.entrada,
  };
};

const money = (value) =>
  value === null || value === undefined || value === ''
    ? null
    : parseCurrency(Number(value));
const yesNo = (value) =>
  value === null || value === undefined ? null : value ? 'Sim' : 'Não';
const percent = (value) =>
  value === null || value === undefined || value === '' ? null : `${value}%`;

const buildItems = (params) => [
  {
    icon: 'account_balance_wallet',
    label: 'Saldo Devedor (PMT+MORA)',
    value: money(params.valor_saldo_devedor),
  },
  {
    icon: 'payments',
    label: 'Valor Renegociado',
    value: money(params.valor_renegociado),
  },
  {
    icon: 'payments',
    label: 'Valor Financiado',
    value: money(params.valor_financiado),
  },
  {
    icon: 'savings',
    label: 'Valor de Entrada',
    value: money(params.valor_entrada),
  },
  {
    icon: 'event',
    label: 'Data 1ª Parcela',
    value: params.data_1parcela
      ? moment(params.data_1parcela).format('DD/MM/YYYY')
      : null,
  },
  { icon: 'percent', label: 'Taxa %', value: percent(params.taxa) },
  { icon: 'event_repeat', label: 'Prazo', value: params.prazo ?? null },
  {
    icon: 'date_range',
    label: 'Periodicidade',
    value: params.periodicidade ? `${params.periodicidade} dias` : null,
  },
  { icon: 'receipt_long', label: '% TAC', value: percent(params.tac) },
  { icon: 'percent', label: '% Taxa Serviço', value: percent(params.tab) },
  {
    icon: 'shield',
    label: 'Seguro Prestamista',
    value: yesNo(params.seguro),
  },
  {
    icon: 'shield',
    label: 'Seguro Assistência',
    value: yesNo(params.seguro_assistencia),
  },
  { icon: 'percent', label: 'IOF?', value: yesNo(params.iof) },
  {
    icon: 'format_list_numbered',
    label: 'Parcelas renegociadas',
    value: Array.isArray(params.parcelas)
      ? params.parcelas.join(', ') || null
      : null,
  },
  {
    icon: 'schedule',
    label: 'Saldo a vencer',
    value: yesNo(params.saldo_a_vencer),
  },
  { icon: 'account_balance', label: 'Bancarizar?', value: params.carteira },
  {
    // Logs anteriores a dispensa da mora nao tem o campo: fica sem valor.
    icon: 'money_off',
    label: 'Mora dispensada',
    value:
      params.dispensa_mora === true
        ? money(params.mora_dispensada_total) || 'Sim'
        : params.dispensa_mora === false
          ? 'Não'
          : null,
  },
];

// Aba Renegociacao do modal de Historico: os dados de cada renegociacao
// concluida que envolve o contrato, lidos do log `renegociacao`. Uma cobranca
// pode ter sido gerada por uma renegociacao (`renegotiationOrigin`) e ter
// gerado outras (`renegotiations`), entao a aba busca pelas duas pontas.
export const Renegotiation = ({ row, onTotal }) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const cobrancaId = row?.id;
  const originId = row?.renegotiationOrigin?.id;

  const init = useCallback(async () => {
    if (!cobrancaId) {
      setLogs([]);
      return;
    }
    setLoading(true);
    try {
      const [asOriginal, asNew] = await Promise.all([
        ReadRenegotiationLogs(cobrancaId, 'cobranca'),
        ReadRenegotiationLogs(cobrancaId, 'cobranca_nova'),
      ]);
      let generatedHere = normalizeResponse(asNew);
      // Log antigo, sem `cobranca_nova`: so e encontrado pela original, e e
      // identificado pelo id da nova gravado nos dados.
      if (!generatedHere.length && originId) {
        const fromOrigin = normalizeResponse(
          await ReadRenegotiationLogs(originId, 'cobranca')
        );
        generatedHere = fromOrigin.filter(
          (log) =>
            Number(log?.dados?.cobranca_nova ?? log?.dados?.nova) ===
            Number(cobrancaId)
        );
      }
      const all = [...generatedHere, ...normalizeResponse(asOriginal)];
      const cancelled = cancelledIds(row);
      const unique = all.filter(
        (log, index) =>
          all.findIndex((item) => item.id === log.id) === index &&
          // Na original, tira as renegociacoes que ela gerou e foram
          // canceladas. Na propria cancelada o log continua, como origem.
          !(
            Number(log?.cobranca?.id) === Number(cobrancaId) &&
            cancelled.includes(
              Number(log?.cobranca_nova?.id ?? log?.dados?.cobranca_nova)
            )
          )
      );
      setLogs(unique);
    } catch (err) {
      console.error('Erro ao carregar as renegociações:', err);
      toast.error('Erro ao carregar os dados da renegociação');
    } finally {
      setLoading(false);
    }
  }, [cobrancaId, originId, row]);

  useEffect(() => init(), [init]);

  useEffect(() => onTotal?.(logs.length), [logs, onTotal]);

  if (loading) {
    return (
      <ListFallback>
        <Load />
      </ListFallback>
    );
  }

  // A cobranca e de renegociacao, mas o log nao foi encontrado (gravado antes
  // da versao que liga as duas pontas e sem o id da nova nos dados).
  if (!logs.length) {
    return (
      <ListFallback>
        {originId ? (
          <UnavailableNote>
            Originada da renegociação do contrato{' '}
            <strong>
              {row?.renegotiationOrigin?.contrato || `#${originId}`}
            </strong>
            . Os dados usados nessa renegociação não estão disponíveis.
          </UnavailableNote>
        ) : (
          <EmptyMessage>Nenhuma renegociação registrada.</EmptyMessage>
        )}
      </ListFallback>
    );
  }

  return (
    <ListContainer>
      {logs.map((log) => {
        const params = readParams(log);
        const isOriginal = Number(log?.cobranca?.id) === Number(cobrancaId);
        const other = isOriginal
          ? params.contrato_novo ||
            log?.cobranca_nova?.contrato ||
            (params.cobranca_nova ? `#${params.cobranca_nova}` : null)
          : params.contrato_original ||
            log?.cobranca?.contrato ||
            (log?.cobranca?.id ? `#${log.cobranca.id}` : null);
        const author =
          log?.usuario?.nome ||
          log?.usuario?.username ||
          log?.usuario_email ||
          '-';
        return (
          <RenegotiationBlock key={log.id}>
            <RenegotiationHeader>
              {isOriginal
                ? `Gerou o contrato ${other || '-'}`
                : `Originada do contrato ${other || '-'}`}
            </RenegotiationHeader>
            <RenegotiationMeta>
              Concluída por {author} em{' '}
              {log?.data ? moment(log.data).format('DD/MM/YYYY HH:mm') : '-'}
              {params.titulos_restantes > 0
                ? ` — renegociação parcial, ${params.titulos_restantes} título(s) seguiram em aberto na original`
                : ''}
            </RenegotiationMeta>
            <SimulationSummary
              pmt={money(params.valor_pmt)}
              items={buildItems(params)}
              title="Dados da renegociação"
              subtitle="Valores e condições usados na conclusão."
            />
          </RenegotiationBlock>
        );
      })}
    </ListContainer>
  );
};
